/**
 * Service API Configuration Entreprise (US-004)
 */

import { api, ApiResponse } from '@e-store/shared';

// Types
export interface EnterpriseAddress {
  street: string;
  street2?: string;
  city: string;
  postalCode?: string;
  country: string;
}

export interface EnterpriseLegalInfo {
  nif: string;
  stat: string;
  rc?: string;
  cf?: string;
  rcs?: string;
  capital?: string;
}

export interface EnterpriseFiscalInfo {
  defaultTvaRate: number;
  currency: string;
  currencySymbol: string;
}

export interface EnterpriseBankInfo {
  bankName?: string;
  accountNumber?: string;
  rib?: string;
  iban?: string;
  swift?: string;
}

export interface EnterpriseConfig {
  id: string;
  version: string;
  name: string;
  slogan?: string;
  description?: string;
  logoUrl?: string;
  logoKey?: string;
  email: string;
  phone: string;
  phone2?: string;
  website?: string;
  address: EnterpriseAddress;
  legal: EnterpriseLegalInfo;
  bank?: EnterpriseBankInfo;
  fiscal: EnterpriseFiscalInfo;
  warrantyMessage?: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface EnterpriseConfigUpdate {
  name: string;
  slogan?: string;
  description?: string;
  logoUrl?: string;
  logoKey?: string;
  email: string;
  phone: string;
  phone2?: string;
  website?: string;
  address: EnterpriseAddress;
  legal: EnterpriseLegalInfo;
  bank?: EnterpriseBankInfo;
  fiscal?: {
    defaultTvaRate?: number;
  };
  warrantyMessage?: string;
}

export interface EnterpriseDefaults {
  isDefault: boolean;
  message: string;
  defaults: {
    currency: string;
    currencySymbol: string;
    defaultTvaRate: number;
    country: string;
  };
}

export interface LogoUploadRequest {
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface LogoUploadResponse {
  uploadUrl: string;
  fileKey: string;
  publicUrl: string;
  expiresIn: number;
}

/**
 * Service API Enterprise
 */
class EnterpriseApiService {
  /**
   * Récupérer la configuration entreprise
   */
  async getConfig(): Promise<ApiResponse<EnterpriseConfig | EnterpriseDefaults>> {
    return api.get<EnterpriseConfig | EnterpriseDefaults>('/api/enterprise');
  }

  /**
   * Mettre à jour la configuration entreprise
   */
  async updateConfig(data: EnterpriseConfigUpdate): Promise<ApiResponse<EnterpriseConfig>> {
    return api.put<EnterpriseConfig>('/api/enterprise', data);
  }

  /**
   * Obtenir une URL présignée pour uploader le logo
   */
  async getLogoUploadUrl(request: LogoUploadRequest): Promise<ApiResponse<LogoUploadResponse>> {
    return api.post<LogoUploadResponse>('/api/enterprise/logo/upload-url', request);
  }

  /**
   * Uploader le logo vers S3 via presigned URL
   */
  async uploadLogoToS3(file: File, uploadUrl: string): Promise<boolean> {
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });
      return response.ok;
    } catch (error) {
      console.error('Erreur upload S3:', error);
      return false;
    }
  }

  /**
   * Supprimer le logo
   */
  async deleteLogo(): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return api.delete<{ success: boolean; message: string }>('/api/enterprise/logo');
  }

  /**
   * Récupérer les infos publiques (sans auth)
   */
  async getPublicInfo(): Promise<ApiResponse<Partial<EnterpriseConfig>>> {
    return api.get<Partial<EnterpriseConfig>>('/api/enterprise/public');
  }
}

export const enterpriseApi = new EnterpriseApiService();
